/**
 * JsonStructureInspector — reusable structure preview + refetch + filter
 * affordance. Used by:
 *
 *   - JsonStructureDetailsDialog (opened from cell/header context menu when
 *     a column is `declared-json`, and from the MetaInfo manager / Settings
 *     "Details" buttons)
 *   - DeclareJsonColumnDialog (pre-declare confirmation)
 *
 * Shows the cached `JsonShapePreview`, a "Refresh from DB" button, and an
 * inline FilterWizard so the user can refine the sample that drives
 * inference (e.g. "only customers with metadata.kind = 'premium'").
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Filter as FilterIcon, Plug, RefreshCw, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import {
  JsonShapePreview,
  type JsonShapeEnumCtx,
  type JsonShapeLooseFkCtx,
} from '../JsonShapePreview';
import { FilterWizard } from '../filters/FilterWizard';
import { useRefetchJsonStructure } from '../../hooks/useRefetchJsonStructure';
import { useInvalidateMetaInfo } from '../../hooks/useMetaInfo';
import { upsertJsonStructureCache } from '../../services/metaInfoApi';
import { fetchTableExplorerRows } from '../../../table-explorer/services/tableExplorerApi';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useConnectionStore } from '../../../../connections/store/connectionStore';
import type {
  JsonStructureCachePath,
  JsonStructureCache,
} from '@/types/electron';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type {
  FilterGroup,
  TableExplorerColumn,
} from '../../../core/types/workspaceTypes';

const newExecutionId = (prefix: string): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? `${prefix}-${crypto.randomUUID()}`
    : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** Locally-tracked sample state — either freshly refetched (pending until
 * the user Applies) or previously applied during this dialog session. */
type SampleSnapshot = {
  paths: JsonStructureCachePath[];
  sampledRows: number;
  sampledAt: string;
  jsonRows: number | null;
};

export type JsonStructureInspectorProps = {
  schema: string;
  table: string;
  column: string;
  /** Cached entry to seed the preview before any refresh has happened. */
  initialCache?: JsonStructureCache | null;
  /** Refetch context — when missing, the refresh/filter affordance hides
   * and only the cached preview is shown. */
  connectionId?: string;
  database?: string;
  engine?: DatabaseEngineId;
  /** Maximum height of the inline shape preview. Defaults to 60vh. */
  maxPreviewHeight?: string;
  /** Analytics source tag (`'details-dialog'`, `'declare-dialog'`, …). */
  source: string;
  /** Pre-fetched columns for the FilterWizard; if omitted, columns from
   * the first refetch response are used. */
  initialColumns?: TableExplorerColumn[];
  /** When provided, JsonShapePreview renders inline enum-state glyphs (or
   *  pick-mode click targets) next to each scalar/array leaf. Driven by the
   *  hosting dialog so the picker UX matches the column-level glyph trio. */
  enumCtx?: JsonShapeEnumCtx;
  /** When set with `pickMode: true`, scalar / array rows in the preview
   *  become click targets that open the loose-FK editor pre-filled with the
   *  picked JSONPath — parallel to `enumCtx.pickMode`. */
  looseFkCtx?: JsonShapeLooseFkCtx;
};

export function JsonStructureInspector(props: JsonStructureInspectorProps) {
  const { connectionId, database, engine } = props;
  const hasContext = Boolean(connectionId && database && engine);

  // Splitting the refetch path into a separate component keeps the
  // QueryClient + analytics hooks out of read-only callers (existing
  // JsonColumnsSection tests render the section without a QueryClientProvider).
  if (hasContext) {
    return <RefetchableInspector {...props} />;
  }
  return <ReadOnlyInspector {...props} />;
}

function ReadOnlyInspector({
  initialCache,
  maxPreviewHeight = '50vh',
  enumCtx,
  looseFkCtx,
}: JsonStructureInspectorProps) {
  const paths = initialCache?.paths ?? [];
  const sampledRows = initialCache?.sampledRows ?? 0;
  const sampledAt = initialCache?.sampledAt ?? null;
  return (
    <div className="space-y-2" data-testid="json-structure-inspector">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span data-testid="json-structure-summary">
          {paths.length} paths · {sampledRows.toLocaleString()} rows
          {sampledAt && <>{' '}· scanned {formatRelativeTime(sampledAt)}</>}
        </span>
      </div>
      {paths.length > 0 ? (
        <JsonShapePreview
          paths={paths}
          defaultExpandDepth={1}
          maxHeight={maxPreviewHeight}
          enumCtx={enumCtx}
          looseFkCtx={looseFkCtx}
        />
      ) : (
        <div
          className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground italic"
          data-testid="json-inspector-empty"
        >
          No JSON paths cached yet.
        </div>
      )}
    </div>
  );
}

function RefetchableInspector({
  schema,
  table,
  column,
  initialCache,
  connectionId,
  database,
  engine,
  maxPreviewHeight = '50vh',
  source,
  initialColumns,
  enumCtx,
  looseFkCtx,
}: JsonStructureInspectorProps) {
  // U31 (A+B merge): an Azure Storage table has no database to refresh from.
  const refreshLabel = engine === 'azurestorage' ? 'Refresh from table' : 'Refresh from DB';
  const [filters, setFilters] = useState<FilterGroup[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  // `pending` — freshly refetched preview, not yet persisted.
  // `applied` — last snapshot the user explicitly Applied this session;
  // overrides `initialCache` for display purposes.
  const [pending, setPending] = useState<SampleSnapshot | null>(null);
  const [applied, setApplied] = useState<SampleSnapshot | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [columnsForFilter, setColumnsForFilter] = useState<TableExplorerColumn[]>(
    initialColumns ?? [],
  );
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [columnsError, setColumnsError] = useState<string | null>(null);
  const { track } = useAnalytics();
  const invalidate = useInvalidateMetaInfo();

  // Settings → Meta Info renders this inspector for any persisted MetaInfo
  // record — including connections that aren't currently open. The IPC for
  // a sample fetch rejects with "Unknown connectionId (not connected)", so
  // detect the disconnected state up-front and offer a Connect affordance
  // instead of a doomed Refresh button.
  const isConnectionLive = useConnectionStore((s) =>
    connectionId
      ? s.activeConnections.some(
          (a) => a.id === connectionId && a.status === 'connected',
        )
      : false,
  );
  const connectionForReconnect = useConnectionStore((s) =>
    connectionId ? s.connections.find((c) => c.id === connectionId) : undefined,
  );
  const connectOperation = useConnectionStore((s) =>
    connectionId ? s.connectionOperationById[connectionId] : undefined,
  );
  const connectError = useConnectionStore((s) =>
    connectionId ? s.connectionErrorById[connectionId] : undefined,
  );
  const connectToDatabase = useConnectionStore((s) => s.connectToDatabase);
  const isConnecting = connectOperation === 'connecting';

  const handleConnect = async () => {
    if (!connectionForReconnect || isConnecting) return;
    track('feature:json_inspector_connect', {
      engine: engine ?? 'unknown',
      source,
    });
    await connectToDatabase(connectionForReconnect);
  };

  const refetchCtl = useRefetchJsonStructure({
    connectionId,
    database,
    schema,
    table,
    column,
    engine,
  });

  useEffect(() => {
    setPending(null);
    setApplied(null);
    setApplyError(null);
  }, [schema, table, column]);

  useEffect(() => {
    if (initialColumns && initialColumns.length > 0) {
      setColumnsForFilter(initialColumns);
    }
  }, [initialColumns]);

  // When the user opens the filter panel and we don't yet have column
  // metadata, fetch it with a tiny `LIMIT 1` query. This avoids forcing
  // the user to click "Refresh from DB" just to populate the column picker
  // (which would also produce an unwanted dirty preview).
  //
  // `isLoadingColumns` is intentionally NOT in the dep array — including it
  // would re-fire this effect when we set it to `true`, cancelling the
  // in-flight promise (cleanup) and then bailing early in the new run, so
  // the loading flag would never clear.
  useEffect(() => {
    if (!showFilters) return;
    if (columnsForFilter.length > 0) return;
    if (!connectionId || !database || !isConnectionLive) return;
    let cancelled = false;
    setIsLoadingColumns(true);
    setColumnsError(null);
    fetchTableExplorerRows({
      connectionId,
      executionId: newExecutionId('json-columns'),
      table: { database, schema, tableName: table },
      columns: '*',
      limit: 1,
      filters: [],
      sorting: [],
    })
      .then((result) => {
        if (cancelled) return;
        if (!result.success) {
          setColumnsError(result.error || 'Failed to load columns');
        } else if ((result.columns?.length ?? 0) > 0) {
          setColumnsForFilter(result.columns ?? []);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setColumnsError(err instanceof Error ? err.message : 'Failed to load columns');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingColumns(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilters, columnsForFilter.length, connectionId, database, schema, table, isConnectionLive]);

  const visible: SampleSnapshot | null = pending ?? applied ?? (
    initialCache
      ? {
          paths: initialCache.paths,
          sampledRows: initialCache.sampledRows,
          sampledAt: initialCache.sampledAt,
          jsonRows: null,
        }
      : null
  );
  const displayedPaths = visible?.paths ?? [];
  const displayedSampledRows = visible?.sampledRows ?? 0;
  const displayedSampledAt = visible?.sampledAt ?? null;
  const displayedJsonRows = visible?.jsonRows ?? null;
  const isDirty = pending !== null;

  const runRefetch = useCallback(
    async (overrideFilters?: FilterGroup[]) => {
      const useFilters = overrideFilters ?? filters;
      setApplyError(null);
      const outcome = await refetchCtl.refetch(useFilters);
      if (!outcome) return;
      // Always replace pending atomically — clicking "Refresh from DB" while
      // a previous preview is dirty must publish the freshly-sampled
      // snapshot (paths + sampledAt) rather than holding onto the stale one.
      setPending({
        paths: outcome.paths,
        sampledRows: outcome.sampledRows,
        sampledAt: outcome.sampledAt,
        jsonRows: outcome.jsonRows,
      });
      if (columnsForFilter.length === 0 && outcome.columns.length > 0) {
        setColumnsForFilter(outcome.columns);
      }
      track('feature:json_structure_refetch', {
        engine: engine ?? 'unknown',
        source,
        filtered: useFilters.length > 0,
      });
    },
    [filters, refetchCtl, columnsForFilter.length, track, engine, source],
  );

  const handleRefetch = () => {
    void runRefetch();
  };

  // Commit-time auto-refresh: FilterWizard only fires `onFiltersChange`
  // when the user actually commits a chip (add / edit / remove / clear).
  // Trigger a fresh sample with the new filter list so the user doesn't
  // have to click "Refresh from DB" as a second step.
  const handleFiltersChange = useCallback(
    (next: FilterGroup[]) => {
      setFilters(next);
      void runRefetch(next);
    },
    [runRefetch],
  );

  const handleApply = async () => {
    if (!pending || !connectionId || !database) return;
    setIsApplying(true);
    setApplyError(null);
    try {
      const entry: JsonStructureCache = {
        schema,
        table,
        column,
        paths: pending.paths,
        sampledRows: pending.sampledRows,
        sampledAt: pending.sampledAt,
      };
      await upsertJsonStructureCache(connectionId, database, entry);
      setApplied(pending);
      setPending(null);
      invalidate(connectionId, database);
      track('feature:json_structure_apply', {
        engine: engine ?? 'unknown',
        source,
      });
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsApplying(false);
    }
  };

  const handleDiscard = () => {
    setPending(null);
    setApplyError(null);
    track('feature:json_structure_discard', {
      engine: engine ?? 'unknown',
      source,
    });
  };

  const filterButtonLabel = useMemo(() => {
    if (!showFilters) {
      return filters.length > 0
        ? `Filter sample (${filters.length})`
        : 'Filter sample';
    }
    return 'Hide filter';
  }, [filters.length, showFilters]);

  return (
    <div className="space-y-2" data-testid="json-structure-inspector">
      <div className="text-xs text-muted-foreground" data-testid="json-structure-summary">
        {displayedPaths.length} paths · {displayedSampledRows.toLocaleString()} rows
        {displayedSampledAt && (
          <>
            {' '}· scanned {formatRelativeTime(displayedSampledAt)}
          </>
        )}
        {displayedJsonRows != null && (
          <>
            {' '}· {displayedJsonRows.toLocaleString()} parseable JSON
          </>
        )}
      </div>

      {/* Filter + Refresh grouped in one subtle bordered card so it's clear
       * the filter modifies the sample query the Refresh button runs. */}
      {!isConnectionLive ? (
        <div
          className="rounded-md border bg-muted/20"
          data-testid="json-inspector-disconnected"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 px-2 py-1.5">
            <span className="text-[11px] text-muted-foreground italic">
              Not connected to this database — connect to refresh the JSON sample.
            </span>
            {connectionForReconnect && (
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={handleConnect}
                disabled={isConnecting}
                className="h-6 px-2 text-[11px]"
                data-testid="json-inspector-connect"
              >
                {isConnecting ? (
                  <span className="mr-1 inline-flex items-center" aria-hidden="true">
                    <RandomDotmSpinner size={12} />
                  </span>
                ) : (
                  <Plug className="h-3 w-3 mr-1" aria-hidden="true" />
                )}
                {isConnecting ? 'Connecting…' : 'Connect'}
              </Button>
            )}
          </div>
          {connectError && !isConnecting && (
            <div
              className="border-t bg-destructive/10 px-2 py-1 text-[11px] text-destructive"
              data-testid="json-inspector-connect-error"
              role="alert"
            >
              {connectError}
            </div>
          )}
        </div>
      ) : (
      <div
        className="rounded-md border bg-muted/20 overflow-hidden"
        data-testid="json-inspector-refresh-card"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 px-2 py-1.5">
          <Button
            size="sm"
            variant="ghost"
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground"
            data-testid="json-inspector-toggle-filter"
          >
            <FilterIcon className="h-3 w-3 mr-1" />
            {filterButtonLabel}
          </Button>
          <Button
            size="sm"
            variant="outline"
            type="button"
            onClick={handleRefetch}
            disabled={refetchCtl.isRefetching || isApplying}
            className="h-6 px-2 text-[11px]"
            data-testid="json-inspector-refetch"
          >
            <RefreshCw
              className={`h-3 w-3 mr-1 ${refetchCtl.isRefetching ? 'animate-spin motion-reduce:animate-none' : ''}`}
              aria-hidden="true"
            />
            {refetchCtl.isRefetching ? 'Refreshing…' : refreshLabel}
          </Button>
        </div>
        {showFilters && (
          <div
            className="border-t bg-background/60 px-2 py-1.5"
            data-testid="json-inspector-filter-panel"
          >
            {columnsForFilter.length === 0 ? (
              isLoadingColumns ? (
                <div className="text-[11px] text-muted-foreground italic">
                  Loading columns…
                </div>
              ) : columnsError ? (
                <div
                  className="text-[11px] text-destructive"
                  data-testid="json-inspector-columns-error"
                >
                  {columnsError}
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground italic">
                  No columns available.
                </div>
              )
            ) : (
              <FilterWizard
                filters={filters}
                columns={columnsForFilter}
                onFiltersChange={handleFiltersChange}
                connectionId={connectionId}
                database={database}
                tableSchema={schema}
                tableName={table}
                engine={engine}
              />
            )}
          </div>
        )}
      </div>
      )}

      {refetchCtl.error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
          data-testid="json-inspector-error"
        >
          {refetchCtl.error}
        </div>
      )}

      {isDirty && pending && (
        <div
          // `key` makes the banner remount when a new sample replaces a
          // previous pending preview — gives the user a visual cue that
          // re-refreshing while dirty actually produced fresh data.
          key={pending.sampledAt}
          // "Unsaved preview" is literally work-in-flight, so it wears the
          // shared --energy hue (CH-09 §6 item 7) instead of a one-off amber.
          // Hue only — the banner's meaning and affordances are unchanged.
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-energy bg-energy-soft px-2 py-1.5 text-[11px] text-foreground"
          data-testid="json-inspector-dirty-banner"
          role="status"
        >
          <span className="font-medium">
            Unsaved preview · sampled {formatRelativeTime(pending.sampledAt)}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={handleDiscard}
              disabled={isApplying}
              className="h-6 px-2 text-[11px]"
              data-testid="json-inspector-discard"
            >
              <Undo2 className="h-3 w-3 mr-1" aria-hidden="true" />
              Discard
            </Button>
            <Button
              size="sm"
              variant="default"
              type="button"
              onClick={handleApply}
              disabled={isApplying}
              className="h-6 px-2 text-[11px]"
              data-testid="json-inspector-apply"
            >
              <Check className="h-3 w-3 mr-1" aria-hidden="true" />
              {isApplying ? 'Saving…' : 'Apply'}
            </Button>
          </div>
        </div>
      )}

      {applyError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
          data-testid="json-inspector-apply-error"
        >
          {applyError}
        </div>
      )}

      {displayedPaths.length > 0 ? (
        <JsonShapePreview
          paths={displayedPaths}
          defaultExpandDepth={1}
          maxHeight={maxPreviewHeight}
          enumCtx={enumCtx}
          looseFkCtx={looseFkCtx}
        />
      ) : (
        <div
          className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground italic"
          data-testid="json-inspector-empty"
        >
          No JSON paths cached yet — click <strong>{refreshLabel}</strong> to
          sample rows now.
        </div>
      )}
    </div>
  );
}
